import React, { useState, useEffect } from 'react';
import { 
    Box, Typography, Modal, TextField, Button, Grid, MenuItem, 
    IconButton, FormControl, InputLabel, Select, ToggleButtonGroup, 
    ToggleButton, Divider, CircularProgress, Checkbox, ListItemText,
    OutlinedInput, Alert
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { RoomService } from '../api/services/roomService';
import { RateService } from '../api/services/rateService';
import { useCurrency } from '../utils/currency';

const CHANNELS = ['Direct', 'OTA', 'Corporate'];

export default function RatePlanFormModal({ open, onClose, onSave, editingPlan = null }) {
    const { currencySymbol } = useCurrency();
    const [formData, setFormData] = useState({
        name: '',
        type: 'Seasonal',
        roomTypeId: '',
        rate: '',
        validTo: '',
        bookingWindowStart: '',
        bookingWindowEnd: '',
        channels: ['Direct'],
        modifier: '',
        minStay: '',
        maxStay: '',
        daysOfWeek: [0, 1, 2, 3, 4, 5, 6],
        promoCode: ''
    });

    const [roomTypes, setRoomTypes] = useState([]);
    const [isLoadingSetup, setIsLoadingSetup] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchSetupData = async () => {
            if (!open) return;
            
            setIsLoadingSetup(true);
            try {
                const authData = JSON.parse(localStorage.getItem('adminAuth') || '{}');
                const tenantId = authData.tenantId || '1';

                const response = await RoomService.getRoomTypes();
                setRoomTypes(response.data || response || []);
            } catch (error) {
                console.error("Failed to fetch room types:", error);
                setError("Failed to load room types. Some options may be missing.");
            } finally {
                setIsLoadingSetup(false);
            }
        };

        fetchSetupData();
    }, [open]);

    useEffect(() => {
        if (editingPlan) {
            // Map incoming data to form state
            setFormData({
                name: editingPlan.name || '',
                type: editingPlan.type || 'Seasonal',
                roomTypeId: editingPlan.roomTypeId || '',
                rate: editingPlan.rate || '',
                validFrom: editingPlan.validFrom ? editingPlan.validFrom.split('.')[0].slice(0, 16) : '',
                validTo: editingPlan.validTo ? editingPlan.validTo.split('.')[0].slice(0, 16) : '',
                bookingWindowStart: editingPlan.bookingWindowStart ? editingPlan.bookingWindowStart.split('.')[0].slice(0, 16) : '',
                bookingWindowEnd: editingPlan.bookingWindowEnd ? editingPlan.bookingWindowEnd.split('.')[0].slice(0, 16) : '',
                channels: Array.isArray(editingPlan.channels) ? editingPlan.channels : ['Direct'],
                modifier: editingPlan.modifier || '',
                minStay: editingPlan.minStay || '',
                maxStay: editingPlan.maxStay || '',
                daysOfWeek: Array.isArray(editingPlan.daysOfWeek) ? editingPlan.daysOfWeek : [0, 1, 2, 3, 4, 5, 6],
                promoCode: editingPlan.promoCode || ''
            });
        } else {
            setFormData({
                name: '',
                type: 'Seasonal',
                roomTypeId: '',
                rate: '',
                validFrom: '',
                validTo: '',
                bookingWindowStart: '',
                bookingWindowEnd: '',
                channels: ['Direct'],
                modifier: '',
                minStay: '',
                maxStay: '',
                daysOfWeek: [0, 1, 2, 3, 4, 5, 6],
                promoCode: ''
            });
        }
        setError(null);
    }, [editingPlan, open]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => {
            const updates = { [name]: value };
            
            if (name === 'roomTypeId') {
                const selectedRoom = roomTypes.find(rt => rt.id === value);
                if (selectedRoom && selectedRoom.baseRate) {
                    updates.rate = selectedRoom.baseRate;
                }
            }
            
            return { ...prev, ...updates };
        });
    };

    const handleTypeChange = (event, newType) => {
        if (newType !== null) {
            setFormData(prev => ({ ...prev, type: newType }));
        }
    };

    const handleChannelChange = (event) => {
        const { value } = event.target;
        setFormData(prev => ({
            ...prev,
            channels: typeof value === 'string' ? value.split(',') : value,
        }));
    };

    const handleDaysOfWeekChange = (event) => {
        const { value } = event.target;
        setFormData(prev => ({
            ...prev,
            daysOfWeek: typeof value === 'string' ? value.split(',').map(Number) : value,
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSaving(true);
        setError(null);

        try {
            const authData = JSON.parse(localStorage.getItem('adminAuth') || '{}');
            const tenantId = authData.tenantId || '1';

            if (editingPlan) {
                // Calculate only changed fields for PATCH
                const updates = {};
                
                if (formData.name !== editingPlan.name) updates.name = formData.name;
                if (formData.type !== editingPlan.type) updates.type = formData.type;
                if (formData.roomTypeId !== editingPlan.roomTypeId) updates.roomTypeId = formData.roomTypeId;
                if (parseFloat(formData.rate) !== parseFloat(editingPlan.rate)) updates.rate = parseFloat(formData.rate);
                
                const isoValidFrom = formData.validFrom ? new Date(formData.validFrom).toISOString() : undefined;
                const isoValidTo = formData.validTo ? new Date(formData.validTo).toISOString() : undefined;
                const isoWindowStart = formData.bookingWindowStart ? new Date(formData.bookingWindowStart).toISOString() : undefined;
                const isoWindowEnd = formData.bookingWindowEnd ? new Date(formData.bookingWindowEnd).toISOString() : undefined;

                if (isoValidFrom !== undefined && isoValidFrom !== editingPlan.validFrom) updates.validFrom = isoValidFrom;
                if (isoValidTo !== undefined && isoValidTo !== editingPlan.validTo) updates.validTo = isoValidTo;
                if (isoWindowStart !== undefined && isoWindowStart !== editingPlan.bookingWindowStart) updates.bookingWindowStart = isoWindowStart;
                if (isoWindowEnd !== undefined && isoWindowEnd !== editingPlan.bookingWindowEnd) updates.bookingWindowEnd = isoWindowEnd;
                
                // Compare arrays for channels
                const channelsChanged = JSON.stringify(formData.channels.sort()) !== JSON.stringify((editingPlan.channels || []).sort());
                if (channelsChanged) updates.channels = formData.channels;

                if (formData.minStay !== '') updates.minStay = parseInt(formData.minStay, 10);
                if (formData.maxStay !== '') updates.maxStay = parseInt(formData.maxStay, 10);
                
                if (formData.type === 'Seasonal' || formData.type === 'Promotional') {
                    updates.daysOfWeek = formData.daysOfWeek;
                } else if (editingPlan.daysOfWeek) {
                    updates.daysOfWeek = null;
                }

                if (formData.type === 'Seasonal') {
                    if (formData.modifier !== '') updates.modifier = parseFloat(formData.modifier);
                } else if (editingPlan.modifier) {
                    updates.modifier = null;
                }

                if (formData.type === 'Promotional') {
                    if (formData.promoCode !== '') updates.promoCode = formData.promoCode;
                } else if (editingPlan.promoCode) {
                    updates.promoCode = null;
                }
                
                if (Object.keys(updates).length > 0) {
                    await RateService.updateRatePlan(editingPlan.id, updates, tenantId);
                }
            } else {
                // Full payload for POST
                const payload = {
                    name: formData.name,
                    type: formData.type,
                    roomTypeId: formData.roomTypeId,
                    rate: parseFloat(formData.rate),
                    channels: formData.channels
                };
                
                if (formData.validFrom) payload.validFrom = new Date(formData.validFrom).toISOString();
                if (formData.validTo) payload.validTo = new Date(formData.validTo).toISOString();
                if (formData.bookingWindowStart) payload.bookingWindowStart = new Date(formData.bookingWindowStart).toISOString();
                if (formData.bookingWindowEnd) payload.bookingWindowEnd = new Date(formData.bookingWindowEnd).toISOString();
                if (formData.minStay !== '') payload.minStay = parseInt(formData.minStay, 10);
                if (formData.maxStay !== '') payload.maxStay = parseInt(formData.maxStay, 10);

                if (formData.type === 'Seasonal' || formData.type === 'Promotional') {
                    payload.daysOfWeek = formData.daysOfWeek;
                }
                
                if (formData.type === 'Seasonal' && formData.modifier !== '') {
                    payload.modifier = parseFloat(formData.modifier);
                }

                if (formData.type === 'Promotional' && formData.promoCode !== '') {
                    payload.promoCode = formData.promoCode;
                }

                await RateService.createRatePlan(payload, tenantId);
            }

            if (onSave) onSave();
            onClose();
        } catch (err) {
            console.error('Failed to save rate plan:', err);
            if (err.response?.status === 403 || err.response?.status === 405) {
                setError("Action not allowed while the hotel is suspended.");
            } else {
                setError(err.response?.data?.message || err.message || 'Failed to save rate plan. Please check your inputs.');
            }
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <Modal open={open} onClose={onClose}>
            <Box sx={{
                position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
                width: { xs: '90%', sm: 650 }, bgcolor: 'background.paper', borderRadius: 3, p: 4, boxShadow: 24,
                maxHeight: '90vh', overflowY: 'auto'
            }}>
                <IconButton sx={{ position: 'absolute', top: 12, right: 12 }} onClick={onClose} disabled={isLoadingSetup || isSaving}>
                    <CloseIcon />
                </IconButton>

                <Typography variant="h6" fontWeight="bold" mb={3}>
                    {editingPlan ? `Edit Rate Plan: ${editingPlan.name}` : 'Create New Rate Plan'}
                </Typography>

                {error && (
                    <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
                        {error}
                    </Alert>
                )}

                {isLoadingSetup ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 5 }}>
                        <CircularProgress />
                        <Typography sx={{ mt: 2 }} color="text.secondary">Loading setup data...</Typography>
                    </Box>
                ) : (
                    <Box component="form" onSubmit={handleSubmit}>
                        <Box sx={{ mb: 3 }}>
                            <Typography variant="caption" color="text.secondary" gutterBottom>Plan Type</Typography>
                            <ToggleButtonGroup
                                value={formData.type}
                                exclusive
                                onChange={handleTypeChange}
                                fullWidth
                                size="small"
                                color="primary"
                            >
                                <ToggleButton value="Base">Base</ToggleButton>
                                <ToggleButton value="Seasonal">Seasonal</ToggleButton>
                                <ToggleButton value="Corporate">Corporate</ToggleButton>
                                <ToggleButton value="Promotional">Promotional</ToggleButton>
                            </ToggleButtonGroup>
                        </Box>

                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <TextField fullWidth label="Plan Name" name="name" value={formData.name} onChange={handleChange} required placeholder="e.g., Early Bird Summer" />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <FormControl fullWidth required>
                                    <InputLabel>Room Type</InputLabel>
                                    <Select name="roomTypeId" value={formData.roomTypeId} label="Room Type" onChange={handleChange}>
                                        {roomTypes.map(t => (
                                            <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField fullWidth label={`Rate per Night (${currencySymbol})`} name="rate" type="number" value={formData.rate} onChange={handleChange} required />
                            </Grid>

                            {formData.type === 'Seasonal' && (
                                <Grid item xs={12} sm={6}>
                                    <TextField fullWidth label={`Modifier (+/- % or ${currencySymbol})`} name="modifier" type="number" value={formData.modifier} onChange={handleChange} helperText="Positive or negative amount" />
                                </Grid>
                            )}

                            {formData.type === 'Promotional' && (
                                <Grid item xs={12} sm={6}>
                                    <TextField fullWidth label="Promo Code" name="promoCode" value={formData.promoCode} onChange={handleChange} />
                                </Grid>
                            )}


                            <Grid item xs={12}>
                                <Divider sx={{ my: 1 }}><Typography variant="caption" color="text.secondary">Validity Period & Stays</Typography></Divider>
                            </Grid>
                            
                            <Grid item xs={12} sm={6}>
                                <TextField fullWidth label="Valid From" name="validFrom" type="datetime-local" value={formData.validFrom} onChange={handleChange} required InputLabelProps={{ shrink: true }} />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField fullWidth label="Valid To" name="validTo" type="datetime-local" value={formData.validTo} onChange={handleChange} required InputLabelProps={{ shrink: true }} />
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <TextField fullWidth label="Min Stay (Nights)" name="minStay" type="number" value={formData.minStay} onChange={handleChange} />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField fullWidth label="Max Stay (Nights)" name="maxStay" type="number" value={formData.maxStay} onChange={handleChange} />
                            </Grid>

                            {(formData.type === 'Seasonal' || formData.type === 'Promotional') && (
                                <Grid item xs={12}>
                                    <FormControl fullWidth>
                                        <InputLabel>Days of Week</InputLabel>
                                        <Select
                                            multiple
                                            name="daysOfWeek"
                                            value={formData.daysOfWeek}
                                            onChange={handleDaysOfWeekChange}
                                            input={<OutlinedInput label="Days of Week" />}
                                            renderValue={(selected) => selected.map(d => ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][d]).join(', ')}
                                        >
                                            {[0,1,2,3,4,5,6].map((day) => (
                                                <MenuItem key={day} value={day}>
                                                    <Checkbox checked={formData.daysOfWeek.indexOf(day) > -1} />
                                                    <ListItemText primary={['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][day]} />
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                            )}

                            <Grid item xs={12}>
                                <Divider sx={{ my: 1 }}><Typography variant="caption" color="text.secondary">Booking Window (Optional)</Typography></Divider>
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <TextField fullWidth label="Window Start" name="bookingWindowStart" type="datetime-local" value={formData.bookingWindowStart} onChange={handleChange} InputLabelProps={{ shrink: true }} />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField fullWidth label="Window End" name="bookingWindowEnd" type="datetime-local" value={formData.bookingWindowEnd} onChange={handleChange} InputLabelProps={{ shrink: true }} />
                            </Grid>

                            <Grid item xs={12}>
                                <FormControl fullWidth>
                                    <InputLabel>Booking Channels</InputLabel>
                                    <Select
                                        multiple
                                        name="channels"
                                        value={formData.channels}
                                        onChange={handleChannelChange}
                                        input={<OutlinedInput label="Booking Channels" />}
                                        renderValue={(selected) => selected.join(', ')}
                                    >
                                        {CHANNELS.map((name) => (
                                            <MenuItem key={name} value={name}>
                                                <Checkbox checked={formData.channels.indexOf(name) > -1} />
                                                <ListItemText primary={name} />
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                        </Grid>

                        <Box sx={{ display: 'flex', gap: 2, mt: 4 }}>
                            <Button fullWidth variant="outlined" onClick={onClose} disabled={isSaving}>Cancel</Button>
                            <Button 
                                type="submit"
                                fullWidth 
                                variant="contained" 
                                disabled={isSaving || isLoadingSetup}
                                startIcon={isSaving && <CircularProgress size={20} color="inherit" />}
                            >
                                {isSaving ? 'Saving...' : (editingPlan ? 'Update Plan' : 'Create Plan')}
                            </Button>
                        </Box>
                    </Box>
                )}
            </Box>
        </Modal>
    );
}
